/**
 * 单帧海报 rawPrompt 拼装(V0 验收3)—— 从 `useFreeCreateActions.handleGenerate` 抽出来的纯函数。
 *
 * ═══ 为什么抽出来 ═══════════════════════════════════════════════════════════════
 * 它原来是 `handleGenerate` 中间的三个 const,夹在 brand/ratio/quota 一堆副作用中间,
 * 想验一句提示词得先 mock 掉 posterService + react-query + navigate + toast。
 * 而【风格】段是 L1 画风与 L2 配色**唯一**到达引擎的通道(事实③:单帧整张由引擎画,
 * 没有 PIL 矢量块,`CreativeOverridesPayload` 也没有 styleTokens 通道)——
 * 唯一通道就该能被直接测,不该藏在副作用后面。
 *
 * ═══ ★ 两条提交路径,以及「归整层吸收了没有」═══════════════════════════════════
 * | 路径 | L1 画风 | L2 配色 / 产品 / 字体档 |
 * |---|---|---|
 * | RAW(没点 AI 优化) | 这里注入【风格】 | 这里注入 |
 * | AI 优化 · 已吸收 | 不注入 | **不注入** —— 渲染层已装进产物内部 |
 * | AI 优化 · 没吸收到 | 不注入 | 照旧注入(宁可重复也不丢) |
 *
 * 2026-07-29(prompt-assembly W1)之前,L2 与产品在 AI 优化路径上**恒注入**,理由是
 * 「归整层拿不到配色与产品字段,不注入就等于用户选了却没生效」。那个理由当时成立 ——
 * `NormalizeContext` 确实只有 industry/brand/userStyle 四个字段。
 *
 * 代价在线上现形(任务 4740607640576):优化产物里已经有一段【风格】(L1 经归整层),
 * 这里追加的 L2 成了**第二段**,两段当场对撞 —— 一个说「石墨黑深色基底」,一个说
 * 「背景 #F8FAFF」,中间还夹着产物自己那句「不要叠加其他风格元素」;产品段则落在
 * 「硬约束」之后,等于在一份签好名的设计简报后面又挂了两页正文。
 *
 * 现在素材进了 `context`,由渲染层写进那唯一一份文档。这里改成**逐项**判断:
 * 归整层确实吸收了这一份(指纹相同)才不注入,吸收不到就照旧注入 —— 用户在优化之后
 * 又换了产品/改了配色、re-run 失败、老草稿回填,全都落在「照旧注入」那一侧。
 */
import type { StyleTokens } from "../../longimage/model/canvas";
import { fontPhraseForKey } from "../../ui/components/free-create/fontStyleOptions";
import { composeStylePrompt, tokensToPrompt } from "../style/tokensToPrompt";
import {
  productPromptSection,
  wantsProductSpecsOnImage,
  type FreeCreateProduct,
} from "./productAdapter";
import { ratioCompositionPhrase } from "./ratioComposition";
import { sliceSafe } from "@/shared/lib/sliceSafe";

/**
 * `rawPrompt` 的真上限 —— `gscq-ai/app/modules/poster/domain/models.py`
 * `raw_prompt: str | None = Field(..., max_length=3500)`。超了是 **422**,
 * 用户只会看到一句"生成失败"。**改这里必须和 ai 侧同一批改**,两边不同步就是 422 之门。
 *
 * ★文案框自己是 `maxLength={PROMPT_BOX_MAX}`,但那只管**用户打的字**;
 * 【风格】(rich tone 200-400 字)、【配色】、【字体风格】都是提交时**追加**上去的,
 * 一个字都不算在那个计数器里。2026-08-03 的病就是两口径同屏打架:框内绿着,提交报超。
 *
 * ★2026-09-09 老板拍板放宽:用户框 2000→3000;总闸/ai 上限 2000→3500 ——
 * 500 的差值就是给追加三段(风格+配色+字体,实测 ≤450)预留的走廊,
 * 「打满文案框再选画风」从此不再必挂。带产品长卖点仍可能顶穿,双口径计数照旧兜底。
 */
export const RAW_PROMPT_MAX = 3500;

/**
 * 「创作描述」输入框上限(用户手打 + 润色回写共用)。
 * 与 RAW_PROMPT_MAX 的差 = 追加段走廊,别把两个数改成一样 —— 那等于把 08-03 的坑填回去。
 */
export const PROMPT_BOX_MAX = 3000;

/**
 * 「归整层吸收了哪一份素材」的指纹(点 AI 优化那一刻拍下)。
 *
 * ★为什么不用 `JSON.stringify` 整个对象:键顺序不是契约(长图模板往返那一批栽过 ——
 * 报「11/11 都变了」其实是键顺序),指纹必须由显式字段拼。
 * ★配色那一段直接借 `tokensToPrompt` 的输出:它本来就是**要发给引擎的那句话**,
 * 拿它当指纹等价于问「这次要发的配色,和优化时发出去的是不是同一句」。
 */
export interface AbsorbedMaterials {
  productCode: string | null;
  styleSignature: string;
}

export function absorbedMaterials(input: {
  product: FreeCreateProduct | null;
  selectedStyle: string | null;
  styleTokens: StyleTokens | null;
  fontStyle: string;
}): AbsorbedMaterials {
  return {
    productCode: input.product?.code ?? null,
    styleSignature: [
      input.selectedStyle ?? "",
      input.styleTokens ? tokensToPrompt(input.styleTokens) : "",
      input.fontStyle,
    ].join("␟"),
  };
}

export interface RawPromptInput {
  /** true = 走两段式 AI 优化产物(带 `[AI-OPTIMIZED]` sentinel,backend 据此跳 minimal 兜底)。 */
  isAiOptimized: boolean;
  /** 用户原文(RAW 路径的正文)。 */
  prompt: string;
  /** AI 优化后的内容描述段(用户可改)。 */
  contentDescription: string;
  /** AI 优化后的系统约束段(只读)。 */
  systemConstraints: string;
  /** L1 画风:`styleOptions` 的 rich tone 文本,或用户自定义描述;null = 自由发挥。 */
  selectedStyle: string | null;
  /** L2 配色;null = 不指定,交给引擎自判。 */
  styleTokens: StyleTokens | null;
  /** 字体档 key(`fontStyleOptions`);auto = 不注入。 */
  fontStyle: string;
  /**
   * 波B:产品库直选的那一份快照;null = 没选产品(存量行为,一个字都不加)。
   * ★产品资料走**确定性拼接**而不是绕一趟 LLM:产品参数一个字都不许编,
   * 而 LLM 会换算数字、会替你把两条卖点归纳成一条。
   */
  product: FreeCreateProduct | null;
  /**
   * 归整层**已经吸收**的那一份素材;null = 没优化过 / 优化失败 / 老草稿回填 ——
   * 一律当作没吸收,照旧追加。
   */
  absorbed: AbsorbedMaterials | null;
  /**
   * 本次生效的画幅 —— 注入【画幅构图】段(见 `ratioComposition`)。
   *
   * ★**两条路都注入,不看 absorbed**:它不是 LLM 产物而是用户的确定性选择,
   *   归整层拿到的只是「已选画幅 3:4」这条输入,产物里并不含这一档的版面法则
   *   (`render.py` 的内容模板没有构图段)。所以优化过也要拼 —— 它不可能与
   *   优化产物"重复",只可能是"缺失"。
   * ★传"提交时真正生效的那个比例"(文案显式 > 手选 > AI 推荐 > 默认),
   *   不是 `form.ratio` 原值;否则图按 9:16 出、指令里写着 1:1 的法则。
   * ★undefined / null / 非枚举值(i2i 跟随原图量出来的怪比例)→ 整段不出现。
   */
  ratio?: string | null;
  /**
   * 「在图上显示产品参数」勾选(2026-10-19)。缺省/false = 参数只作背景资料、不上图;
   * 用户原文点名要尺寸/参数/规格等时视同勾选(见 `wantsProductSpecsOnImage`)。
   */
  showProductSpecs?: boolean;
}

export function buildRawPrompt(input: RawPromptInput): string {
  /**
   * ★0811 报障「提示词被优化没了」(娜娜):优化态此前**只发归整稿**,用户原文一个字
   * 不进指令 —— 归整层丢的任何事实就永远丢了,用户逐字写的话被模板转写顶替。
   * 现在原文以【用户原话】段随行:归整稿管结构,原话管事实,后者优先。原文为空
   * (纯产品库起单)才不带。
   */
  const userWords = input.prompt.trim();
  const base = input.isAiOptimized
    ? `[AI-OPTIMIZED]\n${input.contentDescription.trim()}` +
      (userWords
        ? `\n\n【用户原话(逐字提供的事实与要求;与上文归整稿冲突时,以这里为准)】\n${userWords}`
        : "") +
      `\n\n${input.systemConstraints.trim()}`
    : userWords;

  // ★ 只有 AI 优化路径才谈得上「被吸收」:RAW 路径压根没经过归整层。
  const current = absorbedMaterials(input);
  const absorbed = input.isAiOptimized ? input.absorbed : null;
  const productAbsorbed = absorbed !== null && absorbed.productCode === current.productCode;
  const styleAbsorbed = absorbed !== null && absorbed.styleSignature === current.styleSignature;

  // 【产品】在文案之后、风格之前:先说画什么(事实),再说画成什么样(风格)。
  // 吸收过就不再追加 —— 渲染层已把它分成「要画的字」与「背景资料」两个角色放进文档内部;
  // 这里再追加一份没有角色标注的,会落进「所有数字必须准确出现」的射程。
  const withProduct =
    input.product && !productAbsorbed
      ? `${base}\n【产品】${productPromptSection(input.product, {
          showSpecs: !!input.showProductSpecs || wantsProductSpecsOnImage(input.prompt),
        })}`
      : base;

  // 【画幅构图】在【风格】之前:先定版面结构(主体占多少、留哪条边),再定调性。
  // 反过来写,风格那 200-400 字会把这一行短句盖住(模型对靠后的长段更敏感)。
  //
  // ★2026-07-31:这一段**改写过一次而不是删掉**。原措辞里有「钩子短句 / 行动引导 /
  //   标题」这类**版面角色名词**,引擎把它们当成要画的字印到了成品图上。现在只留几何
  //   (主体居中 / 四周留边 / 上下留空),构图指导照给,但没有能被印上去的东西。
  //   禁词由 `ratioComposition.test.ts` 逐档锁死 —— 加回任何一个角色词都会红。
  // ★2026-09-09:留白句机制整体拆除(老板拍板「都不要有留出空间这样的词了」,
  //   决定与 E-01 回滚锚点记录在 ratioComposition.ts 文件头)——【画幅构图】只剩纯方位词。
  const composition = ratioCompositionPhrase(input.ratio);
  const withRatio = composition ? `${withProduct}\n【画幅构图】${composition}` : withProduct;

  // ★ AI 优化路径把 L1 置 null:它已经通过 userStyle 进过归整层(见文件头表格)。
  //   L2 在**吸收过**时也不再注入 —— 否则就是线上那两段互相打架的【风格】。
  const style = styleAbsorbed
    ? null
    : composeStylePrompt(input.isAiOptimized ? null : input.selectedStyle, input.styleTokens);
  const withStyle = style ? `${withRatio}\n【风格】${style}` : withRatio;

  // 字体档:图像模型据此倾向渲染对应字形。单独一段 —— 与【风格】是两个独立维度,
  // 合并会让"换个画风"把字体一起冲掉(长图那边 fontPair 不跟画风走,同一条口径)。
  const fontPhrase = styleAbsorbed ? "" : fontPhraseForKey(input.fontStyle);
  return fontPhrase ? `${withStyle}\n【字体风格】${fontPhrase}` : withStyle;
}

/**
 * 超出上限多少字(0 = 没超)。
 *
 * ★为什么是"拦下来告诉用户"而不是自动截断:
 * 截尾巴会把【风格】【配色】整段切掉 —— 用户选了、界面亮着、出图没有,
 * 正是本波要消灭的那件事;截文案则是**偷偷删用户写的字**,更糟。
 * 两种"聪明"的处理都不如说清楚:超了多少、可以怎么办。
 */
export function rawPromptOverflow(rawPrompt: string): number {
  return Math.max(0, rawPrompt.length - RAW_PROMPT_MAX);
}

/**
 * 归整产物写回「创作描述」框前的上限(2026-09-08 老板实测:AI 润色后 文案超过上限)。
 *
 * ★这里裁的是 **AI 的改写产物**,不是上面拒绝截断的「用户原话 / 用户选的风格」——
 *   那两样仍走 buildRawPrompt 的追加段、一个字不动。文案框自己 `maxLength` 只拦手打,
 *   程序 setState 回写一个字都拦不住(长图 RefinableField 2026-08-06 同款坑),这里把回写
 *   也收进**文案框**上限(不是总闸上限:回写落进框里,就得按框的尺寸裁)。
 *   裁到句末标点避免半句;找不到近处的边界(< 60% 上限)才硬切。
 */
export function capPolishResult(text: string, max = PROMPT_BOX_MAX): string {
  if (text.length <= max) return text;
  const head = sliceSafe(text, max);
  const lastTerm = Math.max(
    head.lastIndexOf("。"),
    head.lastIndexOf("！"),
    head.lastIndexOf("？"),
    head.lastIndexOf("\n")
  );
  return (lastTerm >= max * 0.6 ? head.slice(0, lastTerm + 1) : head).trimEnd();
}
